import React, { useMemo, useState } from "react";
import { encode, decode } from "blurhash";
import { useCachedMedia } from "@/lib/mediaCache";

// Encode a compact BlurHash + capture the image's natural dimensions from a File.
export async function encodeImageMeta(file) {
  try {
    const bitmap = await createImageBitmap(file);
    const w = bitmap.width, h = bitmap.height;
    const scale = Math.min(1, 32 / Math.max(w, h)); // tiny canvas → cheap, fast encode
    const sw = Math.max(1, Math.round(w * scale));
    const sh = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = sw; canvas.height = sh;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, sw, sh);
    const data = ctx.getImageData(0, 0, sw, sh);
    const hash = encode(data.data, sw, sh, 4, 3);
    bitmap.close && bitmap.close();
    return { blur_hash: hash, media_w: w, media_h: h };
  } catch {
    return {};
  }
}

// Decode a BlurHash into a small data URL usable as an instant placeholder.
export function blurhashToDataURL(hash, w = 32, h = 32) {
  try {
    const pixels = decode(hash, w, h);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    const img = ctx.createImageData(w, h);
    img.data.set(pixels);
    ctx.putImageData(img, 0, 0);
    return canvas.toDataURL();
  } catch {
    return null;
  }
}

// <img> that shows an instant BlurHash placeholder, then fades in the real (cached) image.
export function BlurImage({ src, hash, className, imgClassName, onClick, testid, alt = "", onNaturalSize }) {
  const resolved = useCachedMedia(src);
  const [loaded, setLoaded] = useState(false);
  const bg = useMemo(() => (hash ? blurhashToDataURL(hash) : null), [hash]);
  return (
    <div className={`relative overflow-hidden ${className || ""}`} onClick={onClick}>
      {bg && !loaded && <img src={bg} aria-hidden alt="" className="absolute inset-0 w-full h-full object-cover" />}
      {!bg && !loaded && <div aria-hidden className="absolute inset-0 bg-[var(--rc-surface-2)] animate-pulse" />}
      <img src={resolved} alt={alt} data-testid={testid}
        onLoad={(e) => { setLoaded(true); if (onNaturalSize && e.target.naturalWidth) onNaturalSize(e.target.naturalWidth, e.target.naturalHeight); }}
        className={`relative ${imgClassName || ""} transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`} />
    </div>
  );
}
